import { Box } from '@chakra-ui/react';
import { CustomOnResize, CustomOnResizeEnd, CustomOnResizeStart } from 'modules/Editor/components/BoundingBox';
import { useBoundingBox } from 'modules/Editor/components/BoundingBox/useBoundingBox';
import { useAppSelector } from 'modules/Editor/store';
import { selectActiveWidgets, selectIsActiveWidget } from 'modules/Editor/store/widgetSelector';
import { useEffect, useRef } from 'react';
import { useCallback } from 'react';
import { isLeftSideHandle, isSideHandle } from 'utils/boundingBox';
import { isTargetStyleNumber } from 'utils/dom';
import {
  ResponsiveWidgetToolbar,
  TransparentImg,
  WidgetBaseProp,
  WidgetSideMenuSettings,
  useEditor,
  useWidget,
} from 'widgets/sdk';

import { ReadOnlyResponsiveWidgetBase, ResponsiveWidgetBase } from '../ResponsiveWidgetBase';
import { MultipleChoices } from './MultipleChoices';
import { ComponentWidgetIdKeys, MultipleChoicesWidgetData } from './MultipleChoicesWidget.types';
import { MultipleChoicesWidgetSideMenuSettings } from './MultipleChoicesWidgetSideMenuSettings';
import { MultipleChoicesWidgetToolbar } from './toolbar';

/**
 * MultipleChoicesWidget with edit mode
 * @returns
 */
export const MultipleChoicesWidget = ({ getWidgetMemberComponent }: WidgetBaseProp) => {
  const { openWidgetSideMenu } = useEditor();
  const { widgetId, componentWidgetIdMap, memberWidgetIds } = useWidget<MultipleChoicesWidgetData>();

  const { boundingBox } = useBoundingBox();
  const maxResizeTranslateX = useRef<null | number>(null);

  const activeWidgets = useAppSelector(selectActiveWidgets);
  const isWidgetSelected = useAppSelector(selectIsActiveWidget(widgetId));
  const questionWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.QuestionText];
  const isQuestionWidgetSelected = useAppSelector(selectIsActiveWidget(questionWidgetId));
  const isAnswerSelected = activeWidgets.some((data) => memberWidgetIds.includes(data.id));
  const isMemberSelected = isQuestionWidgetSelected || isAnswerSelected;
  const isToolbarEnabled = isWidgetSelected && !isMemberSelected;
  const startLeftRightPx = useRef<[number, number]>([0, 0]);

  const customOnResizeStart = useCallback(({ event, onResizeStart }: CustomOnResizeStart) => {
    onResizeStart(event);

    const target = event.target;
    if (target instanceof HTMLElement) {
      startLeftRightPx.current = [target.offsetLeft, target.offsetLeft + target.offsetWidth];
    }
  }, []);

  const customOnResize = useCallback(({ event, frameMap }: CustomOnResize) => {
    const isSide = isSideHandle(event.direction);
    const target = event.target as HTMLElement;
    const frame = frameMap.get(event.target);
    const [newTranslateX, newTranslateY] = event.drag.beforeTranslate;
    const [distX] = event.dist;

    // Ignore to update current widget style if value is a string(auto, fit-content, min-content, max-content, etc)
    if (isTargetStyleNumber(target.style.width)) target.style.width = `${event.width}px`;
    if (isTargetStyleNumber(target.style.height)) target.style.height = `${event.height}px`;

    // After changing the width above, check whether the maximum width has been exceeded.
    const enableMinWidth = target.clientWidth < target.scrollWidth;
    if (enableMinWidth) {
      target.style.width = `${target.scrollWidth}px`;
    }

    // Update Translate
    const isLeftHandle = isSide && isLeftSideHandle(event.direction);
    if (isLeftHandle) {
      if (enableMinWidth) {
        const [startLeftPx, startRightPx] = startLeftRightPx.current;
        const currentRightPx = target.offsetLeft + startLeftPx + distX + target.scrollWidth;
        const diff = currentRightPx - startRightPx;
        const newTranslateX = startLeftPx + distX - diff;

        target.style.transform = `translate(${newTranslateX}px, ${0}px) rotate(0deg)`;
        frame.translate = [newTranslateX, 0];
      } else {
        target.style.transform = `translate(${-distX}px, ${0}px) rotate(0deg)`;
        frame.translate = [-distX, 0];
      }
    } else {
      target.style.transform = `translate(${newTranslateX}px, ${newTranslateY}px) rotate(0deg)`;
      frame.translate = [newTranslateX, newTranslateY];
    }
  }, []);

  const customOnResizeEnd = useCallback(
    ({ onResizeEnd, event }: CustomOnResizeEnd) => {
      onResizeEnd(event);
      event.target.style.height = 'fit-content';
      maxResizeTranslateX.current = null;

      setTimeout(() => boundingBox.updateRect(), 0);
    },
    [boundingBox],
  );

  const handleDoubleClick = useCallback(() => {
    if (isMemberSelected) return;

    openWidgetSideMenu();
  }, [isMemberSelected, openWidgetSideMenu]);

  useEffect(() => {
    // Update Bounding Box Rectangle to Member Widget, on Type Change
    setTimeout(() => boundingBox.updateRect(), 0);
  }, [boundingBox]);
  return (
    <ResponsiveWidgetBase
      fitWidthWidgets={memberWidgetIds}
      widgetIdRenderOrder={memberWidgetIds}
      onResizeStart={customOnResizeStart}
      onResize={customOnResize}
      onResizeEnd={customOnResizeEnd}
      onDoubleClick={handleDoubleClick}
    >
      <ResponsiveWidgetToolbar isActive={isToolbarEnabled}>
        <MultipleChoicesWidgetToolbar />
      </ResponsiveWidgetToolbar>
      <WidgetSideMenuSettings>
        <MultipleChoicesWidgetSideMenuSettings />
      </WidgetSideMenuSettings>
      <MultipleChoices getWidgetMemberComponent={getWidgetMemberComponent!} />
    </ResponsiveWidgetBase>
  );
};

/**
 * MultipleChoicesWidget with read-only mode
 * @returns
 */
export const ReadOnlyMultipleChoicesWidget = ({ getWidgetMemberComponent, includeAltTextImg }: WidgetBaseProp) => {
  const { memberWidgetIds } = useWidget<MultipleChoicesWidgetData>();

  return (
    <ReadOnlyResponsiveWidgetBase fitWidthWidgets={memberWidgetIds}>
      {includeAltTextImg && <TransparentImg />}
      <Box>
        <MultipleChoices getWidgetMemberComponent={getWidgetMemberComponent!} isReadOnly />
      </Box>
    </ReadOnlyResponsiveWidgetBase>
  );
};
